import React, {Component} from 'react';
import Swipe from 'ui-components/Swipe';
import {motion} from 'framer-motion';
import {getStarColor, randomColor} from 'utils/colors';
import StarWarpsLogo from 'components/StarWarpsLogo';
import {
  INVERT_STARFIELD,
  RANDOM_STAR_COLOR_EVERY
} from 'constants';
import KeyShortcut from './KeyShortcut';

const hexPart = n => n.toString(16).padStart(2,'0');


// @keyListeners()
export default class Starfield extends Component{
  
  static defaultProps = {
    numStars: 600,
    redirectTime: 5000,
    // speedUp: 0.3,
    speedUp: 0.5,
    starRatio: 256,
    warpBase: 15,
    randomColors: false,
    wheelIncrement: 0.2,
    canvasStyle: {
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: "100%",
      height: "100%",
    },
    showLogo: true,
    // logo animations
    startDistance: -20000,
    logoDistance: -1000,
    warpOutDistance: 2000,
    // star background opacity
    animationSteps: 200,
    fillOpacityStart: 0.75,
    fillOpacityEnd: 0.1,
    maxSizeMult: 2,
    invert: false,
    orientationEventsOn: false
  };

  constructor(props){
    super(props);
    
    this.star = [];
    this.starSpeed = 4;
    this.cursor = this.getCenter();
    this.mouse = {...this.cursor};

    this.canvasx = 0;
    this.canvasy = 0;

    this.state = {
      redirecting: false,
      ready: false,
      fadeOut: false,
      inverted: props.invert,
      touchMode: props.orientationEventsOn,
      paused: false
    }
  }

  componentDidMount(){
    let {
      orientationEventsOn,
      preloadGallery
    } = this.props;
    let frame = document.getElementById('frame');
    if (frame){
      frame.style.overflow = 'hidden';
    }
    document.body.style.overflow = "hidden";

    window.addEventListener("resize", this.onResize);
    if (orientationEventsOn){
      window.addEventListener("deviceorientation", this.handleOrientation, true);
    }

    window.addEventListener("focus", this.onFocus);
    window.addEventListener("blur", this.onBlur);
    
    this.mounted = true;

    if (typeof preloadGallery === 'function'){
      preloadGallery();
    }

  }

    componentWillUnmount(){
    let {orientationEventsOn} = this.props;
      let frame = document.getElementById('frame');
      if (frame){
        frame.style.overflow = 'auto';
      }
      document.body.style.overflow = "auto";

    window.removeEventListener("resize", this.onResize);
    if (orientationEventsOn) {
      window.removeEventListener("deviceorientation", this.handleOrientation);
    }

    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("blur", this.onBlur);

    // Clean up timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }

      this.mounted = false;
    }

  onBlur = () => {
    this.setState({paused: true})
  }

  onFocus = () => {
    this.setState({
      paused: false
    }, () => this.anim())
  }

    handleOrientation = (evt) => {
      let {
        absolute,
        alpha,
        beta,
        gamma
      } = evt;
      let {touchMode} = this.state;
      // alpha = the motion of the device around the z axis, represented in degrees with values ranging from 0 to 360.
      // beta = the motion of the device around the x axis, represented in degrees with values ranging from -180 to 180. This represents a front to back motion of the device.
      // gamma = the motion of the device around the y axis, represented in degrees with values ranging from -90 to 90. This represents a left to right motion of the device.
      // this.doc.width
      // this.doc.height
      // 0 = this.doc.width / 2;
      if (touchMode){
        this.cursor.x = ((gamma + 90) / 180) * this.doc.width;
        this.cursor.y = ((beta + 90) / 360) * this.doc.height;
      }
      else{
        window.removeEventListener("deviceorientation", this.handleOrientation);
      }
    }

  getCenter = (integersOnly=true) => {
    this.doc = {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight
    };
    let center = {
      x: this.doc.width * 0.5,
      y: this.doc.height * 0.5
    }
    if (integersOnly){
      center.x = Math.round(center.x);
      center.y = Math.round(center.y);
    }
    return center;
  }

  onResize = e => {
    // Debounce resize to avoid regenerating 600 stars multiple times
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      let center = this.getCenter();
      this.cursor = {...center}
      this.perspectiveOrigin = {
        ...center,
        z: (this.doc.width + this.doc.height) * 0.5
      };
      this.star_color_ratio = 1 / this.perspectiveOrigin.z;

      this.init();
    }, 150);
  }

  invertStarfield = () => {
    if (this.mounted){
      this.setState((state) => ({
        inverted: !state.inverted
      }), () => {
        this.updateFillStyle();
        // Update all star colors when inverting
        this.star.forEach(s => {
          if (s.colorHex === '#ffffff') {
            s.colorHex = '#000000';
            s.color.r = 0;
            s.color.g = 0;
            s.color.b = 0;
            s.cachedRGBA = 'rgba(0, 0, 0, ';
          } else if (s.colorHex === '#000000') {
            s.colorHex = '#ffffff';
            s.color.r = 255;
            s.color.g = 255;
            s.color.b = 255;
            s.cachedRGBA = 'rgba(255, 255, 255, ';
          }
        });
      });
    }
  }

  init = () => {
    let {
      numStars,
      randomColors,
      invert,
      fillOpacityStart,
      maxSizeMult
    } = this.props;
    let {
      canvas,
      context,
      inverted
    } = this.state;

    for(var i=0; i < numStars; i++){
      this.star[i] = {
        x: 		Math.random()
                * (this.doc.width  * 2)
                - this.perspectiveOrigin.x*2,
        y:  		Math.random()
                * (this.doc.height * 2)
                - this.perspectiveOrigin.y*2,
        z:  		Math.round(Math.random()
                * this.perspectiveOrigin.z),
        xsave: 		0,
        ysave: 		0,
        color:  	randomColors ? randomColor() : getStarColor({
          invert: inverted,
          randomEvery: RANDOM_STAR_COLOR_EVERY,
          randomGrays: false
        }),
        sizeMult: 	(Math.random() * (maxSizeMult - 0.5)) + 0.5
      };
      let {r, g, b} = this.star[i].color;
      this.star[i].colorHex = `#${hexPart(r)}${hexPart(g)}${hexPart(b)}`;
      // Cache RGBA prefix for performance (alpha will be appended)
      this.star[i].cachedRGBA = `rgba(${r}, ${g}, ${b}, `;
    }

    canvas.width = this.doc.width;
    canvas.height = this.doc.height;

    this.fillOpacity = fillOpacityStart;
    this.updateFillStyle();
    context.strokeStyle = inverted
      ? 'rgba(255,255,255,1)'
      : 'rgba(0,0,0,1)'

    // setTimeout(function(){
      if (this.mounted){
        this.setState({ready: true});
      }
    // }.bind(this), 2000);
    if (!this.started){
      this.started = true;
      this.anim();
    }
  }


  getBackgroundColor = () => {
    let shade = this.state.inverted ? 255 : 0;
    return `rgba(${shade}, ${shade}, ${shade}, ${this.fillOpacity})`;
  }

  updateFillStyle = () => {
    this.state.context.fillStyle = this.getBackgroundColor();
  }

  anim = () => {
    let {
      numStars,
      starRatio,
      speedUp
    } = this.props;
    let {
      canvas,
      context,
      inverted,
      paused
    } = this.state;

    if (!this.mounted){
      return;
    }

    // Handle fill opacity animation (integrated from setInterval)
    if (this.fillAnimData) {
      const elapsed = Date.now() - this.fillAnimData.startTime;
      const progress = Math.min(elapsed / this.fillAnimData.duration, 1);

      this.fillOpacity = this.fillAnimData.startOpacity -
        (this.fillAnimData.startOpacity - this.fillAnimData.endOpacity) * progress;

      context.fillStyle = this.getBackgroundColor();
      this.starSpeed += speedUp;

      if (progress >= 1) {
        this.fillAnimData = null;
      }
    }

    this.mouse.x = this.cursor.x - this.perspectiveOrigin.x;
    this.mouse.y = this.cursor.y - this.perspectiveOrigin.y;



    context.fillRect(0, 0, this.doc.width, this.doc.height);

    let t;
    var s;
    for(let i = 0; i < numStars; i++){
      s = this.star[i];
      t = true;
      this.star_x_save = s.xsave;
      this.star_y_save = s.ysave;

      s.x += this.mouse.x >> 4; 
      if(s.x > this.perspectiveOrigin.x << 1){
        s.x -= this.doc.width << 1;
        t = false;
      }
      if(s.x < -this.perspectiveOrigin.x << 1){
        s.x += this.doc.width<<1;
        t = false;
      }

      s.y += this.mouse.y >> 4; 
      if(s.y > this.perspectiveOrigin.y << 1){
        s.y -= this.doc.height << 1;
        t = false; 
      } 
      if(s.y < -this.perspectiveOrigin.y << 1){
        s.y += this.doc.height << 1;
        t = false;
      }

      s.z -= this.starSpeed; 
      if(s.z > this.perspectiveOrigin.z){
        s.z -= this.perspectiveOrigin.z;
        t = false;
      }
      s.color.a = 1
        - (s.z / this.perspectiveOrigin.z)
        + 0.25;

      if(s.z < 0) {
        s.z += this.perspectiveOrigin.z;
        t = false;
      }
      s.xsave = this.perspectiveOrigin.x
        + (s.x / s.z) * starRatio;
      s.ysave = this.perspectiveOrigin.y
        + (s.y / s.z) * starRatio;
      
      if(	t &&
        (this.star_x_save > 0) && (this.star_x_save < this.doc.width) &&
        (this.star_y_save > 0) && (this.star_y_save < this.doc.height)){
          // Use cached RGBA prefix + alpha for performance
          this.setStarColor(s.cachedRGBA + s.color.a + ')')

          context.lineWidth = (
            (1 - this.star_color_ratio * s.z)
            * 2) * s.sizeMult * (inverted ? 2 : 1);

          context.beginPath();
          context.moveTo(this.star_x_save, this.star_y_save);
          context.lineTo(s.xsave, s.ysave);
          context.stroke();
          context.closePath();
      }
    }
    if (!paused){
      window.requestAnimationFrame(this.anim);
    }
  }

  speedUpTo = (destSpeed, currentSpeed, inc, everyms, callback) => {
      if (currentSpeed >= destSpeed){
          if (typeof callback === 'function'){
            callback.call(null);
          }
      }
      else{
        this.starSpeed = currentSpeed + inc;
          setTimeout( function () {
        if (this.mounted){
          this.speedUpTo(
            destSpeed,
            this.starSpeed,
            inc,
            everyms,
            callback
          ); 
        }
          }.bind(this), everyms);
      }
  }

  warpOut = () => {
    let {
      speedUp,
      warpBase
    } = this.props;
    if (this.redirecting){
      this.warpAndRedirect(); 
    }
    if (this.starSpeed < warpBase){
      this.speedUpTo(warpBase, this.starSpeed, speedUp, 5, () => {
        this.warpAndRedirect(); 
      });
    } else {
      this.warpAndRedirect();
    }
  }

  setStarColor = color => {
    let {
      context,
      inverted
    } = this.state;
    if (context) {
      if (color !== this.lastStarColor){
        context.strokeStyle = color; 
        this.lastStarColor = color;
      }
    }
  }

  toGallery = () => {
    let {navigateTo} = this.props;
    navigateTo('/gallery/about');
  }

  warpAndRedirect = () => {
    let {
      fillOpacityStart,
      fillOpacityEnd,
      redirectTime
    } = this.props;

    if (this.redirecting){
      // Cancel ongoing animation
      this.fillAnimData = null;
      if (this.redirectTimeout) {
        clearTimeout(this.redirectTimeout);
      }
      this.toGallery();
    }
    else{
      this.redirecting = true;

      // Use requestAnimationFrame-based animation instead of setInterval
      this.fillAnimData = {
        startTime: Date.now(),
        duration: redirectTime / 2,
        startOpacity: fillOpacityStart,
        endOpacity: fillOpacityEnd
      };

      if (this.mounted){
        this.setState({redirecting: true});
      }
      setTimeout(function(){
        if (this.mounted){
          this.setState({fadeOut: true});
        }
      }.bind(this), redirectTime / 2)

      this.redirectTimeout = setTimeout( function(){
        this.toGallery();
      }.bind(this), redirectTime);
    }
  }

  canvasLoaded = canvas => {
    if (canvas){
      // keeping the canvas and context in state
      // instead of this prevents it being wiped out
      // on state change
      this.setState({
        canvas,
        context: canvas.getContext('2d')
      }, () => {
        this.onResize();
      });
    }
  }

  mouseMove = (e) => {
    let {
      redirecting,
      touchMode
    } = this.state;
    if (touchMode && this.mounted){
      this.setState({touchMode: false});
    }
    if (!redirecting){
      // Reuse cursor object instead of creating new one
      this.cursor.x = e.pageX - this.canvasx;
      this.cursor.y = e.pageY - this.canvasy;
    }
  }

  getLogoStyles = () => {
    let {
      startDistance,
      logoDistance,
      warpOutDistance
    } = this.props;
    let {
      ready,
      redirecting
    } = this.state;

    let distance;
    let rotation = 0;
    if (redirecting){
      distance = warpOutDistance;
      rotation = -75;
    }
    else if (ready){
      distance = logoDistance;
      rotation = 0;
    }
    else{
      distance = startDistance;
    }

    return {
      rotation,
      distance,
      opacity: ready ? 1 : 0
    };
  }

  getLogoTransition = () => {
    let {redirecting} = this.state;

    if (redirecting){
      return {
        type: "spring",
        stiffness: 15,
        damping: 20
      };
    }
    return {
      type: "spring",
      stiffness: 10,
      damping: 14
    };
  }

  getCanvasStyles = () => {
    let {
      ready,
      fadeOut
    } = this.state;

    return {
      opacity: fadeOut || !ready ? 0 : 1
    };
  }

  getCanvasTransition = () => {
    let {ready} = this.state;

    if (!ready){
      return {
        type: "spring",
        stiffness: 50,
        damping: 25
      };
    }
    return {
      type: "spring",
      stiffness: 34,
      damping: 15
    };
  }

  onWheel = e => {
    let {
      wheelIncrement
    } = this.props;
    let evt = e.target;

    let delta=0;

    if ( evt.wheelDelta ) {
      delta = evt.wheelDelta / 120;
    } else if (evt.detail){
      delta =- evt.detail / 3;
    }
    
    this.starSpeed += (delta>=0) ? -wheelIncrement : wheelIncrement;
    
    if(evt.preventDefault){
      evt.preventDefault();
    }
  }

  render(){
    let {
      redirecting,
      ready,
      inverted
    } = this.state;
    let {
      canvasStyle,
      showLogo,
      startDistance
    } = this.props;
    return (
      <div
        className="wrap-starfield"
        onWheel={this.onWheel}
      >
        <KeyShortcut k=" " action={this.warpOut} />
        <KeyShortcut k="i" action={this.invertStarfield} />
        <Swipe
          onTap={this.invertStarfield}
          onSwipeRight={this.warpOut}
          onSwipeLeft={this.warpOut}
          onMouseMove={this.mouseMove}
          onClick={this.warpOut}
        >

          <motion.canvas
            id="starfield"
            key="starfield"
            ref={this.canvasLoaded}
            initial={{opacity: 0}}
            animate={this.getCanvasStyles()}
            transition={this.getCanvasTransition()}
            style={canvasStyle}
            className="starfield"
          />
          {showLogo && (() => {
            const {distance, rotation, opacity} = this.getLogoStyles();
            return (
              <div style={{
                perspective: '1000px',
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
              }}>
                <motion.div
                  initial={{
                    translateZ: startDistance,
                    opacity: 0,
                    rotateX: 0
                  }}
                  animate={{
                    translateZ: distance,
                    rotateX: rotation,
                    opacity: opacity
                  }}
                  transition={this.getLogoTransition()}
                  style={{
                    top: '50%',
                    height: '100%'
                  }}
                >
                    <StarWarpsLogo
                      className="logo"
                      color={inverted ? "black" : undefined}
                    />
                </motion.div>
              </div>
            );
          })()}
        </Swipe>
      </div>
    )
  }

}